import { rawLocalStorage } from "@sfc/ui/local-storage";
import { useSyncExternalStore } from "react";

/**
 * Global impersonation state, in its own leaf module. Extracted from
 * ImperativeState to break a module cycle: trpc.ts needs `impersonationRef`
 * (to attach the acting-as header) while ImperativeState needs trpc's base-URL
 * helpers — with both in one file, whichever module loads first leaves the
 * other reading partially-evaluated exports, and bun's evaluation order for
 * that cycle shifts with unrelated import changes ("Export named 'trpc' not
 * found" test failures). This module imports nothing app-side, so it can't
 * re-enter a cycle.
 */

const GLOBAL_IMPERSONATION_KEY = "impersonateUserId";
// Per-round app modes (viewAsUserId/viewAsTrackMember/readOnly) and the
// recent-target lists — cleared together with the global key: they are all
// account-level impersonation state.
export const APP_MODE_STORAGE_PREFIX = "appMode-";
export const RECENT_IMPERSONATE_PREFIX = "impersonateRecent-";
export const GLOBAL_RECENT_KEY = "impersonateRecent";
// The account that owns ALL local impersonation state — including per-round
// modes, which can exist without the global key (View as Track Member).
const STATE_OWNER_KEY = "impersonationStateOwner";

export type GlobalImpersonation = {
  userId: string;
  displayName: string;
  // The impersonated user's own SFC-admin flag, captured when impersonation
  // starts. Mirrors the server, which resolves effectiveIsSfcAdmin from the
  // TARGET's flag — so viewing as an admin keeps admin surfaces visible.
  // Missing (older persisted entries) means false. Client-side courtesy only;
  // every admin route re-checks on the server.
  isSfcAdmin?: boolean;
  // The impersonated user's own email, captured alongside the admin flag so
  // the engine's actor carries the target's. Missing on older persisted
  // entries.
  email?: string;
  // The underlying (real) user who set this. Impersonation is account-level
  // state: a different account signing in on this device must not inherit it.
  setByUserId?: string;
};

export function getGlobalImpersonation(): GlobalImpersonation | null {
  if (!rawLocalStorage.isSupported()) {
    return null;
  }

  const raw = rawLocalStorage.getItem(GLOBAL_IMPERSONATION_KEY);

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);

    if (
      typeof parsed === "object" &&
      parsed &&
      typeof parsed.userId === "string"
    ) {
      return {
        userId: parsed.userId,
        displayName:
          typeof parsed.displayName === "string"
            ? parsed.displayName
            : parsed.userId,
        isSfcAdmin: parsed.isSfcAdmin === true ? true : undefined,
        email: typeof parsed.email === "string" ? parsed.email : undefined,
        setByUserId:
          typeof parsed.setByUserId === "string"
            ? parsed.setByUserId
            : undefined,
      };
    }

    // Backwards compat: bare string from older format
    if (typeof parsed === "string") {
      return { userId: parsed, displayName: parsed };
    }
  } catch {
    // Bare string (not JSON-wrapped)
    if (typeof raw === "string" && raw) {
      return { userId: raw, displayName: raw };
    }
  }

  return null;
}

// Render-stable snapshot of the persisted entry, kept in lockstep by
// setGlobalImpersonation (every writer in this tab goes through it), the owner
// reconcile, and storage events from other tabs. A component that read
// storage directly at render never re-rendered on a sweep — the listener
// snapshot (the ref) can be unchanged null→null.
let cachedGlobalImpersonation: GlobalImpersonation | null | undefined;

// The live signed-in user last confirmed by reconcileImpersonationOwner. A
// change from another tab only restores the ref once this tab has confirmed
// the owner itself — the same rule as a page load.
let confirmedRealUserId: string | null = null;

function readGlobalImpersonationSnapshot(): GlobalImpersonation | null {
  if (cachedGlobalImpersonation === undefined) {
    cachedGlobalImpersonation = getGlobalImpersonation();
  }

  return cachedGlobalImpersonation;
}

/** Re-read the persisted entry into the snapshot. Keeps the previous object
 * when nothing changed, so subscribers don't re-render. Returns whether it
 * changed. */
function syncCachedGlobalImpersonation(): boolean {
  const next = getGlobalImpersonation();
  const previous = cachedGlobalImpersonation;

  if (
    previous !== undefined &&
    previous?.userId === next?.userId &&
    previous?.displayName === next?.displayName &&
    previous?.isSfcAdmin === next?.isSfcAdmin &&
    previous?.email === next?.email &&
    previous?.setByUserId === next?.setByUserId
  ) {
    return false;
  }

  cachedGlobalImpersonation = next;
  return true;
}

function readImpersonationStateOwner(): string | null {
  try {
    return rawLocalStorage.getItem(STATE_OWNER_KEY);
  } catch {
    return null;
  }
}

function isImpersonationOwnedBy(
  realUserId: string,
  owner: string | null,
  entry: GlobalImpersonation | null,
) {
  return owner === realUserId || entry?.setByUserId === realUserId;
}

export function setGlobalImpersonation(value: GlobalImpersonation | null) {
  cachedGlobalImpersonation = value;

  if (!rawLocalStorage.isSupported()) {
    return;
  }

  try {
    if (value) {
      rawLocalStorage.setItem(GLOBAL_IMPERSONATION_KEY, JSON.stringify(value));

      if (value.setByUserId) {
        stampImpersonationStateOwner(value.setByUserId);
      }
    } else {
      rawLocalStorage.removeItem(GLOBAL_IMPERSONATION_KEY);
    }
  } catch (e) {
    console.warn("Failed to persist impersonation state:", e);
  }
}

/** Record which account owns the local impersonation state. Called by every
 * writer — including per-round modes, which exist without the global key. */
export function stampImpersonationStateOwner(userId: string) {
  try {
    rawLocalStorage.setItem(STATE_OWNER_KEY, userId);
  } catch {
    // Non-critical.
  }
}

/**
 * Read by the tRPC client (TRPCProvider / trpcVanilla) to attach the
 * `x-acting-as-user-id` header so the server can resolve effective identity.
 * Written by ClientWrapper.setMode().
 *
 * Deliberately NOT initialized from localStorage: persisted impersonation may
 * belong to a previous account (sessions end without the sign-out page), so
 * the header stays off until the live identity confirms the owner —
 * reconcileImpersonationOwner restores it on match. Round pages restore
 * their own per-round mode independently (works offline).
 *
 * Deliberate trade: the owner's own refresh briefly renders self-view on
 * non-round pages until the confirm lands. Restoring from the CACHED
 * identity instead would close that flash but reopen the cross-account
 * header leak exactly when it matters (a new sign-in still carries the
 * previous user's cache until the first getCurrentUser resolves).
 */
export const impersonationRef: { current: string | null } = {
  current: null,
};

/** Reactive read of the persisted global impersonation: re-renders on every
 * notified change, including a sweep that leaves the ref unchanged. Private —
 * UI reads useActiveGlobalImpersonation, which hides entries this tab isn't
 * acting on. */
function useGlobalImpersonationValue(): GlobalImpersonation | null {
  return useSyncExternalStore(
    subscribeImpersonationListener,
    readGlobalImpersonationSnapshot,
    () => null,
  );
}

/** Subscribers notified when impersonationRef.current changes via setMode(). */
const impersonationListeners = new Set<() => void>();

export function notifyImpersonationListeners() {
  for (const listener of impersonationListeners) {
    listener();
  }
}

/** Subscribe to impersonationRef change notifications. Returns unsubscribe.
 * While anything is subscribed, changes other tabs make to the persisted
 * impersonation are applied here too, so every tab starts and stops
 * impersonating together. */
export function subscribeImpersonationListener(cb: () => void): () => void {
  if (impersonationListeners.size === 0 && typeof window !== "undefined") {
    window.addEventListener("storage", handleImpersonationStorageChange);
  }

  impersonationListeners.add(cb);

  return () => {
    impersonationListeners.delete(cb);

    if (impersonationListeners.size === 0 && typeof window !== "undefined") {
      window.removeEventListener("storage", handleImpersonationStorageChange);
    }
  };
}

/**
 * Another tab changed the persisted impersonation (localStorage is shared by
 * every tab, but `storage` events fire only in the others). Refresh the
 * snapshot and the ref the tRPC clients read. Only a change to the entry moves
 * the ref, so a ref a round restored on its own (before the owner reconcile,
 * or offline) survives writes that leave the entry as it was. The ref follows
 * the new entry only when this tab has already confirmed its owner — an entry
 * from an unconfirmed account waits for this tab's own reconcile, which
 * restores or sweeps it.
 */
function handleImpersonationStorageChange(event: StorageEvent) {
  if (event.key !== null && event.key !== GLOBAL_IMPERSONATION_KEY) {
    return;
  }

  if (!syncCachedGlobalImpersonation()) {
    return;
  }

  const entry = readGlobalImpersonationSnapshot();
  impersonationRef.current =
    entry &&
    confirmedRealUserId !== null &&
    isImpersonationOwnedBy(
      confirmedRealUserId,
      readImpersonationStateOwner(),
      entry,
    )
      ? entry.userId
      : null;
  notifyImpersonationListeners();
}

/**
 * React hook that subscribes to impersonationRef changes.
 * Unlike ImpersonateContext, this is NOT cleared on RoundWrapper unmount,
 * so it's safe to use as a dependency for the Round reload effect.
 */
export function useImpersonationRefValue(): string | null {
  return useSyncExternalStore(
    subscribeImpersonationListener,
    () => impersonationRef.current,
    () => null, // server snapshot — no impersonation during SSR
  );
}

/**
 * The persisted entry this tab is actually acting on — the one the acting-as
 * header carries — or null. The effective user, the "Viewing as" banner, and
 * the impersonation picker all read this, so none can claim an impersonation
 * the others don't: an entry waiting on the owner reconcile shows nothing,
 * while the ref restores that bypass it (round entry, offline) show
 * everywhere at once.
 */
export function useActiveGlobalImpersonation(): GlobalImpersonation | null {
  const ref = useImpersonationRefValue();
  const entry = useGlobalImpersonationValue();

  return ref !== null && entry?.userId === ref ? entry : null;
}

/**
 * Stop impersonating entirely: remove the persisted global key, reset the
 * in-memory ref the tRPC clients read, and notify subscribers (banner,
 * header) so the UI updates immediately.
 */
export function clearGlobalImpersonation() {
  setGlobalImpersonation(null);
  impersonationRef.current = null;
  notifyImpersonationListeners();
}

/**
 * Remove every piece of persisted impersonation state on this device: the
 * global key, per-round app modes, and recent-target lists. Kept here (the
 * leaf module) so the owner reconcile below can sweep without importing
 * app modules.
 */
export function clearAllImpersonationLocalState() {
  try {
    const keysToRemove: string[] = [];

    for (let i = 0; i < rawLocalStorage.length; i++) {
      const key = rawLocalStorage.key(i);

      if (
        key &&
        (key.startsWith(APP_MODE_STORAGE_PREFIX) ||
          key.startsWith(RECENT_IMPERSONATE_PREFIX) ||
          key === GLOBAL_RECENT_KEY)
      ) {
        keysToRemove.push(key);
      }
    }

    for (const key of keysToRemove) {
      rawLocalStorage.removeItem(key);
    }

    rawLocalStorage.removeItem(STATE_OWNER_KEY);
  } catch {
    // Non-critical; the global key below still clears.
  }

  setGlobalImpersonation(null);
  impersonationRef.current = null;
}

/**
 * Runs when the live signed-in user resolves. A persisted impersonation set
 * by this account is restored (the header stays off until then); one set by
 * a different account — or unstamped legacy state — is swept entirely,
 * including per-round modes, which would otherwise restore the previous
 * account's view-as state inside rounds.
 */
export function reconcileImpersonationOwner(realUserId: string) {
  if (!rawLocalStorage.isSupported()) {
    return;
  }

  confirmedRealUserId = realUserId;
  const owner = readImpersonationStateOwner();
  const current = getGlobalImpersonation();

  if (isImpersonationOwnedBy(realUserId, owner, current)) {
    // The entry may have been written by another tab since this one last
    // read it: the banner's snapshot must follow the ref it restores.
    const snapshotChanged = syncCachedGlobalImpersonation();
    const refChanged =
      current != null && impersonationRef.current !== current.userId;

    if (refChanged) {
      impersonationRef.current = current.userId;
    }

    if (snapshotChanged || refChanged) {
      notifyImpersonationListeners();
    }

    return;
  }

  // A different owner, an unstamped legacy global entry, or — with no stamp
  // at all — any per-round mode carrying view-as state (View as Track Member
  // writes only appMode-<roundId>): all swept.
  const hasImpersonationState =
    owner != null ||
    current != null ||
    (() => {
      try {
        for (let i = 0; i < rawLocalStorage.length; i++) {
          const key = rawLocalStorage.key(i);

          if (!key?.startsWith(APP_MODE_STORAGE_PREFIX)) {
            continue;
          }

          const raw = rawLocalStorage.getItem(key);

          if (!raw) {
            continue;
          }

          try {
            const mode = JSON.parse(raw);

            if (mode?.viewAsUserId != null || mode?.viewAsTrackMember != null) {
              return true;
            }
          } catch {
            // Unparseable mode: treat as suspect state.
            return true;
          }
        }
      } catch {
        return false;
      }

      return false;
    })();

  if (!hasImpersonationState) {
    return;
  }

  clearAllImpersonationLocalState();
  notifyImpersonationListeners();
}
